import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function AdminVATSettingsForm({ vatSetting, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    country: '',
    countryCode: '',
    rate: '',
    isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (vatSetting) {
      setFormData({
        country: vatSetting.country || '',
        countryCode: vatSetting.countryCode || '',
        rate: vatSetting.rate ?? '',
        isActive: vatSetting.isActive ?? true,
      });
    }
  }, [vatSetting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onSave({
        country: formData.country.trim().toUpperCase(),
        countryCode: formData.countryCode.trim().toUpperCase(),
        rate: Number(formData.rate),
        isActive: formData.isActive,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save VAT setting');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">
            {vatSetting ? 'Edit VAT Setting' : 'Add VAT Setting'}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-slate-700 mb-2">
                  Country *
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={100}
                  placeholder="e.g. PORTUGAL"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 uppercase"
                />
              </div>

              <div>
                <label htmlFor="countryCode" className="block text-sm font-medium text-slate-700 mb-2">
                  Country Code (ISO) *
                </label>
                <input
                  type="text"
                  id="countryCode"
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  minLength={2}
                  maxLength={2}
                  placeholder="e.g. PT"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 uppercase"
                />
                <p className="mt-1 text-xs text-slate-500">2-character ISO 3166-1 alpha-2 code</p>
              </div>
            </div>

            <div>
              <label htmlFor="rate" className="block text-sm font-medium text-slate-700 mb-2">
                VAT Rate (%) *
              </label>
              <input
                type="number"
                id="rate"
                name="rate"
                value={formData.rate}
                onChange={handleChange}
                required
                disabled={loading}
                min={0}
                max={100}
                step="0.01"
                placeholder="e.g. 23"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
              />
            </div>

            {vatSetting && (
              <div className="flex items-center">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Active</span>
                </label>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Saving...' : vatSetting ? 'Update VAT Setting' : 'Create VAT Setting'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
